import React, { useState } from 'react';
import { format } from 'date-fns';
import { safeFormat } from '@/lib/safeDate';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RefreshCw, Eye } from 'lucide-react';
import { useWebhookLogs, type WebhookLog } from '@/hooks/useWebhookLogs';
import { useWebhookConfigs } from '@/hooks/useWebhookConfigs';

const STATUS_COLORS: Record<string, string> = {
  success: 'bg-emerald-100 text-emerald-800',
  error: 'bg-red-100 text-red-800',
  rejected: 'bg-amber-100 text-amber-800',
  skipped: 'bg-slate-100 text-slate-800',
};

export function WebhookLogsViewer() {
  const [webhookFilter, setWebhookFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);

  const { data: configs } = useWebhookConfigs();
  const { data: logs, isLoading, refetch, isFetching } = useWebhookLogs({
    webhookName: webhookFilter !== 'all' ? webhookFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    limit: 200,
  });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={webhookFilter} onValueChange={setWebhookFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Todos os webhooks" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os webhooks</SelectItem>
            {configs?.map(c => (
              <SelectItem key={c.webhook_name} value={c.webhook_name}>
                {c.display_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Todos os status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="success">Sucesso</SelectItem>
            <SelectItem value="error">Erro</SelectItem>
            <SelectItem value="rejected">Rejeitado</SelectItem>
            <SelectItem value="skipped">Ignorado</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>

        <span className="text-xs text-muted-foreground ml-auto">
          {logs?.length ?? 0} registros
        </span>
      </div>

      {/* Logs Table */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : !logs?.length ? (
        <p className="text-center text-muted-foreground py-8">Nenhum log encontrado.</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium text-muted-foreground">Data</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Webhook</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Tempo</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Dry Run</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Erro</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-muted/30 transition-colors">
                    <td className="p-3 text-xs whitespace-nowrap">
                      {safeFormat(new Date(log.created_at), 'dd/MM HH:mm:ss')}
                    </td>
                    <td className="p-3 font-mono text-xs">{log.webhook_name}</td>
                    <td className="p-3">
                      <Badge variant="secondary" className={STATUS_COLORS[log.status] || ''}>
                        {log.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-xs">
                      {log.processing_time_ms ? `${log.processing_time_ms}ms` : '—'}
                    </td>
                    <td className="p-3">
                      {log.is_dry_run && (
                        <Badge variant="outline" className="text-xs">teste</Badge>
                      )}
                    </td>
                    <td className="p-3 text-xs text-red-600 max-w-[200px] truncate">
                      {log.error_message || '—'}
                    </td>
                    <td className="p-3">
                      <Button variant="ghost" size="icon" onClick={() => setSelectedLog(log)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Detalhes do Log</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <ScrollArea className="max-h-[65vh]">
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-muted-foreground">Webhook:</span> {selectedLog.webhook_name}</div>
                  <div><span className="text-muted-foreground">Status:</span> {selectedLog.status}</div>
                  <div><span className="text-muted-foreground">Tempo:</span> {selectedLog.processing_time_ms}ms</div>
                  <div><span className="text-muted-foreground">Dry Run:</span> {selectedLog.is_dry_run ? 'Sim' : 'Não'}</div>
                  <div><span className="text-muted-foreground">Contato:</span> {selectedLog.contact_id || '—'}</div>
                  <div><span className="text-muted-foreground">Deal:</span> {selectedLog.deal_id || '—'}</div>
                  <div><span className="text-muted-foreground">IP:</span> {selectedLog.ip_address || '—'}</div>
                  <div><span className="text-muted-foreground">Data:</span> {safeFormat(new Date(selectedLog.created_at), 'dd/MM/yyyy HH:mm:ss')}</div>
                </div>

                {selectedLog.error_message && (
                  <div>
                    <p className="text-muted-foreground mb-1">Erro:</p>
                    <pre className="bg-red-50 text-red-800 rounded p-3 text-xs whitespace-pre-wrap">
                      {selectedLog.error_message}
                    </pre>
                  </div>
                )}

                {selectedLog.request_payload && (
                  <div>
                    <p className="text-muted-foreground mb-1">Request Payload:</p>
                    <pre className="bg-muted rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(selectedLog.request_payload, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedLog.response_payload && (
                  <div>
                    <p className="text-muted-foreground mb-1">Response:</p>
                    <pre className="bg-muted rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(selectedLog.response_payload, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
